import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, ArrowRight, ArrowLeft, Sparkles, Wrench, Zap, Building2, Rocket, Phone, Plug, Calendar, Mail, ShieldCheck, FlaskConical, ListChecks, CreditCard, MessageSquare, CalendarClock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServicesEditor } from "@/components/dashboard/ServicesEditor";
import { ServiceDocumentUpload } from "@/components/knowledge/ServiceDocumentUpload";
import { SquareCardForm } from "@/components/dashboard/SquareCardForm";
import VerifyPrompt from "@/components/auth/VerifyPrompt";
import WebsiteImport from "@/components/onboarding/WebsiteImport";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PLAN_LIST, planById, DONE_FOR_YOU_FEE, TRIAL_DAYS } from "@/components/pricingData";
import { toast } from "sonner";

const STEPS = ["Plan", "Setup", "Business", "Services", "Integrations", "Phone", "Create"];
const ALL_LANGS = [["English", "🇬🇧"], ["Spanish", "🇪🇸"], ["French", "🇫🇷"], ["Arabic", "🇸🇦"]];
const VOICES = ["Multilingual (Auto)", "Emma (Female, British)", "James (Male, British)", "Olivia (Female, US)", "Noah (Male, US)"];
const CAT_ICON = { calendar: Calendar, booking: CalendarClock, email: Mail, payment: CreditCard, messaging: MessageSquare, crm: Building2 };

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState(params.get("plan") || "professional");
  const [setupOption, setSetupOption] = useState("self");
  const [cfg, setCfg] = useState(null);
  const [servicesText, setServicesText] = useState("");
  const [templates, setTemplates] = useState({});
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState({ option: "existing", business_number: "" });
  const [integrations, setIntegrations] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [billing, setBilling] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const reloadBusiness = () => api.get("/business").then((r) => {
    setCfg(r.data); setServicesText((r.data.services || []).join(", "));
    if (r.data.service_list && r.data.service_list.length) setServiceList(r.data.service_list);
  }).catch(() => {});

  const loadBilling = () => api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/business").then((r) => {
      setCfg(r.data); setServicesText((r.data.services || []).join(", "));
      if (r.data.service_list && r.data.service_list.length) setServiceList(r.data.service_list);
    }).catch(() => {});
    api.get("/subscription").then((r) => { if (r.data.plan) setPlan(r.data.plan); if (r.data.setup_option) setSetupOption(r.data.setup_option); }).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data.templates || {})).catch(() => {});
    api.get("/phone").then((r) => { if (r.data.phone_setup) setPhone({ option: "existing", business_number: "", ...r.data.phone_setup }); }).catch(() => {});
    api.get("/integrations").then((r) => setIntegrations(r.data.integrations || [])).catch(() => {});
    api.get("/billing/config").then((r) => setBillingConfig(r.data)).catch(() => {});
    loadBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const p = planById(plan);

  useEffect(() => {
    if (params.get("billing") === "verified") {
      setStep(6);
      loadBilling();
      toast.success("Payment method verified — you can now create your AI receptionist.");
    } else if (params.get("billing") === "failed") {
      setStep(6);
      toast.error("Card verification wasn't completed. Please try again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTemplate = (typeKey) => {
    const t = templates[typeKey];
    if (!t) { set("business_type", typeKey); return; }
    const greeting = (t.greeting || "").replace("{business}", cfg.name || t.name);
    setCfg({
      ...cfg, business_type: typeKey, industry: t.name, greeting,
      faqs: t.faqs || [], style: t.style || "",
      voice: t.voice || cfg.voice || "Multilingual (Auto)",
      hours: cfg.hours || t.hours || "",
    });
    setServicesText((t.services || []).join(", "));
    if (!serviceList.length) {
      setServiceList((t.services || []).map((n) => ({ id: n, name: n, price: "", duration: null, description: "", enabled: true })));
    }
    toast.success(`${t.name} template loaded — greeting, services, voice & hours suggested. Edit anything you like.`);
  };

  const toggleLang = (l) => {
    const cur = cfg.languages || [];
    set("languages", cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]);
  };

  const savePlan = async () => {
    await api.post("/subscription/select-plan", { plan, setup_option: setupOption });
  };

  const saveBusiness = async () => {
    const clean = serviceList.filter((s) => s.name && s.name.trim());
    const payload = {
      ...cfg,
      services: servicesText.split(",").map((s) => s.trim()).filter(Boolean),
      service_list: clean,
    };
    await api.put("/business", payload);
  };

  const savePhone = async () => {
    await api.put("/phone", { option: phone.option, business_number: phone.business_number || "", ai_number: phone.ai_number || "" });
  };

  const next = async () => {
    setBusy(true);
    try {
      if (step === 1) await savePlan();
      if (setupOption === "done_for_you" && step === 1) {
        navigate("/onboarding/done-for-you");
        return;
      }
      if (step === 2 || step === 3) await saveBusiness();
      if (step === 5) await savePhone();
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Something went wrong, please try again");
    } finally {
      setBusy(false);
    }
  };

  const connectTool = async (it) => {
    try {
      const r = await api.post("/integrations/connect", { provider: it.key });
      if (r.data.status === "connected") toast.success(r.data.message);
      else toast.info(r.data.message);
      const l = await api.get("/integrations"); setIntegrations(l.data.integrations || []);
    } catch (e) { toast.error("Could not connect right now"); }
  };

  const verifyCardToken = async (token) => {
    setBusy(true);
    try {
      await savePlan();
      await api.post("/billing/verify-card", { plan, token });
      toast.success("Payment method verified — £1 charged");
      await loadBilling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not verify your card");
    } finally {
      setBusy(false);
    }
  };

  const createReceptionist = async () => {
    if (!billing?.card_verified) { toast.error("Please verify your payment method first"); return; }
    setBusy(true);
    try {
      await saveBusiness();
      await savePhone();
      await api.post("/business/create-assistant");
      toast.success("🎉 Your AI Receptionist has been created — test it before going live!");
      navigate("/dashboard/ai-receptionist");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create your AI. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (user && user.email_verified === false) return <VerifyPrompt />;
  if (!cfg) return <div className="min-h-screen flex items-center justify-center text-slate-400 gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-1.5 justify-center mb-8">
          <img src="/operamind-mark.png" alt="OperaMind" className="h-11 w-11 object-contain" />
          <div className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-xl tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-900">Intelligence. Orchestrated.</span>
          </div>
        </div>

        {/* stepper */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${i <= step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 w-8 sm:w-12 ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
          {showImport && <WebsiteImport onClose={() => setShowImport(false)} onApplied={async () => { await reloadBusiness(); setShowImport(false); toast.success("Website imported — review the details below and continue."); }} />}
          {step === 0 && (
            <div data-testid="onboard-plan">
              <h2 className="font-heading text-2xl font-bold tracking-tight">Choose your plan</h2>
              <p className="text-slate-500 text-sm mt-1">Start with a {TRIAL_DAYS}-day free trial. Change anytime.</p>
              <div className="mt-5 space-y-3">
                {PLAN_LIST.map((pl) => (
                  <button key={pl.id} data-testid={`onboard-plan-${pl.id}`} onClick={() => setPlan(pl.id)}
                    className={`w-full text-left rounded-xl border p-4 flex items-center justify-between transition-colors ${plan === pl.id ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                    <div>
                      <p className="font-semibold text-slate-900 flex items-center gap-2">{pl.name} {pl.popular && <span className="text-xs bg-blue-600 text-white rounded-full px-2 py-0.5">Popular</span>}</p>
                      <p className="text-sm text-slate-500">{pl.minutes}</p>
                    </div>
                    <div className="text-right"><span className="font-heading text-xl font-extrabold">£{pl.price}</span><span className="text-slate-500 text-sm">/mo</span></div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboard-setup">
              <h2 className="font-heading text-2xl font-bold tracking-tight">How would you like to set up?</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <button data-testid="setup-self" onClick={() => setSetupOption("self")} className={`text-left rounded-xl border p-5 transition-colors ${setupOption === "self" ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                  <Sparkles className="h-6 w-6 text-emerald-600" />
                  <p className="mt-3 font-semibold">Self-service</p>
                  <p className="text-sm text-slate-500 mt-1">Configure it yourself now.</p>
                  <p className="mt-3 font-heading font-extrabold">Free</p>
                </button>
                <button data-testid="setup-dfy" onClick={() => setSetupOption("done_for_you")} className={`text-left rounded-xl border p-5 transition-colors ${setupOption === "done_for_you" ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                  <Wrench className="h-6 w-6 text-blue-600" />
                  <p className="mt-3 font-semibold">Done-for-you</p>
                  <p className="text-sm text-slate-500 mt-1">We set everything up for you.</p>
                  <p className="mt-3 font-heading font-extrabold">£{DONE_FOR_YOU_FEE} <span className="text-sm font-medium text-slate-500">one-time</span></p>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboard-business">
              <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Building2 className="h-6 w-6 text-blue-600" /> Your business</h2>
              <p className="text-slate-500 text-sm mt-1">Choose how to set up — import from your website, upload a price list, or enter details manually.</p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="setup-methods">
                <button data-testid="method-website" onClick={() => setShowImport(true)} className="text-left rounded-xl border-2 border-blue-500 bg-blue-50/50 p-4 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center gap-1.5"><Globe className="h-5 w-5 text-blue-600" /><span className="text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-100 rounded px-1.5 py-0.5">Recommended</span></div>
                  <p className="mt-2 font-semibold text-sm">🌐 Import From Website</p>
                  <p className="text-xs text-slate-500 mt-0.5">Enter your URL — we auto-configure everything.</p>
                </button>
                <button data-testid="method-pdf" onClick={() => document.getElementById("pdf-upload-anchor")?.scrollIntoView({ behavior: "smooth" })} className="text-left rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors">
                  <FlaskConical className="h-5 w-5 text-emerald-600" />
                  <p className="mt-2 font-semibold text-sm">📄 Upload PDF / Price List</p>
                  <p className="text-xs text-slate-500 mt-0.5">We read your document to extract services.</p>
                </button>
                <button data-testid="method-manual" className="text-left rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors">
                  <Wrench className="h-5 w-5 text-slate-600" />
                  <p className="mt-2 font-semibold text-sm">✍️ Manual Setup</p>
                  <p className="text-xs text-slate-500 mt-0.5">Fill in the fields below yourself.</p>
                </button>
              </div>
              <p className="text-slate-500 text-sm mt-6">Pick your business type to load a tailored template, then edit anything.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Business type</Label>
                  <Select value={cfg.business_type || ""} onValueChange={applyTemplate}>
                    <SelectTrigger data-testid="onboard-business-type" className="mt-1"><SelectValue placeholder="Select your business type" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(templates).map(([k, t]) => <SelectItem key={k} value={k}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Business name</Label><Input data-testid="onboard-name" value={cfg.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Phone</Label><Input data-testid="onboard-business-phone" value={cfg.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Call forwarding number</Label><Input data-testid="onboard-forwarding" value={cfg.forwarding_number || ""} onChange={(e) => set("forwarding_number", e.target.value)} placeholder="For transferring urgent calls" className="mt-1" /></div>
                <div>
                  <Label className="text-xs">Preferred AI voice</Label>
                  <Select value={cfg.voice || "Multilingual (Auto)"} onValueChange={(v) => set("voice", v)}>
                    <SelectTrigger data-testid="onboard-voice" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-xs">Opening hours</Label><Input value={cfg.hours || ""} onChange={(e) => set("hours", e.target.value)} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Greeting</Label><Textarea value={cfg.greeting || ""} onChange={(e) => set("greeting", e.target.value)} rows={2} className="mt-1" /></div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Languages the AI can speak</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ALL_LANGS.map(([l, flag]) => {
                      const on = (cfg.languages || []).includes(l);
                      return (
                        <button key={l} type="button" data-testid={`onboard-lang-${l}`} onClick={() => toggleLang(l)}
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                          <span aria-hidden>{flag}</span> {l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboard-services">
              <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><ListChecks className="h-6 w-6 text-blue-600" /> Services &amp; pricing</h2>
              <p className="text-slate-500 text-sm mt-1">Add the services you offer. Prices, durations and descriptions are optional — your AI only quotes what you approve here and never invents anything.</p>
              <div className="mt-5">
                <ServicesEditor value={serviceList} onChange={setServiceList} />
              </div>
              <div id="pdf-upload-anchor" className="mt-6 rounded-xl border border-blue-200 bg-blue-50/40 p-5">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-900">Upload a menu or price list</h3>
                  <span className="text-[10px] font-bold bg-blue-600 text-white rounded-full px-2 py-0.5">FASTEST</span>
                </div>
                <p className="text-sm text-slate-500 mt-1 mb-3">Drag &amp; drop a menu, price list, brochure or catalogue — OperaMind extracts your services automatically for you to review and add above. Files are also saved to your AI's knowledge.</p>
                <ServiceDocumentUpload onAdd={(svcs) => setServiceList((prev) => [...prev, ...svcs])} />
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="onboard-phone">
              <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Phone className="h-6 w-6 text-blue-600" /> Business phone setup</h2>
              <p className="text-slate-500 text-sm mt-1">How would you like customers to reach your AI Receptionist?</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <button data-testid="phone-existing" onClick={() => setPhone({ ...phone, option: "existing" })} className={`text-left rounded-xl border p-5 transition-colors ${phone.option === "existing" ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                  <Phone className="h-6 w-6 text-emerald-600" />
                  <p className="mt-3 font-semibold">Use my existing number <span className="text-xs bg-emerald-600 text-white rounded-full px-2 py-0.5 ml-1">Recommended</span></p>
                  <p className="text-sm text-slate-500 mt-1">Keep your current number — we'll show you how to forward calls to your AI.</p>
                </button>
                <button data-testid="phone-operamind" onClick={() => setPhone({ ...phone, option: "operamind" })} className={`text-left rounded-xl border p-5 transition-colors ${phone.option === "operamind" ? "border-blue-600 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                  <Sparkles className="h-6 w-6 text-blue-600" />
                  <p className="mt-3 font-semibold">Get an OperaMind number</p>
                  <p className="text-sm text-slate-500 mt-1">We provision a new dedicated business number linked to your AI.</p>
                </button>
              </div>
              {phone.option === "existing" ? (
                <div className="mt-4">
                  <Label className="text-xs">Your current business phone number</Label>
                  <Input data-testid="phone-business-number" value={phone.business_number || ""} onChange={(e) => setPhone({ ...phone, business_number: e.target.value })} placeholder="+44 20 7946 0000" className="mt-1" />
                  <p className="text-xs text-slate-400 mt-2">After activation we'll give you simple call-forwarding instructions so your AI answers incoming calls.</p>
                </div>
              ) : (
                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                  A dedicated OperaMind number will be assigned automatically when you activate your AI. You can use it as your business number right away.
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="onboard-tools">
              <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Plug className="h-6 w-6 text-blue-600" /> Integrations</h2>
              <p className="text-slate-500 text-sm mt-1">Which tools do you use to run your business? Connect them securely, or skip — OperaMind Calendar works out of the box.</p>
              <div className="mt-5 space-y-2 max-h-[360px] overflow-y-auto pr-1">
                {integrations.map((it) => {
                  const Icon = CAT_ICON[it.category] || Plug;
                  return (
                    <div key={it.key} data-testid={`onboard-integration-${it.key}`} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 shrink-0"><Icon className="h-4.5 w-4.5" /></div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-slate-900 truncate">{it.name}{it.is_default && " · Default"}</p>
                          <p className="text-xs text-slate-500 capitalize">{it.category}{it.optional ? " · optional" : ""}</p>
                        </div>
                      </div>
                      {it.coming_soon
                        ? <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1 shrink-0">Coming soon</span>
                        : it.connected
                          ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1 shrink-0">Connected</span>
                          : <Button data-testid={`onboard-connect-${it.key}`} size="sm" variant="outline" onClick={() => connectTool(it)} className="shrink-0">Connect</Button>}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-3">Prefer to skip? Just click Continue — you can connect these anytime later.</p>
            </div>
          )}

          {step === 6 && (
            <div data-testid="onboard-create" className="text-center py-4">
              <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white mx-auto"><FlaskConical className="h-8 w-8" /></div>
              <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight">Create your AI Receptionist</h2>
              <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
                We'll build your own dedicated AI configured for <span className="font-semibold text-slate-900">{cfg.name}</span> and take you to
                <span className="font-semibold text-slate-900"> Test Mode</span> — try it by voice and text before it goes live. No calls are charged and your
                <span className="font-semibold text-slate-900"> {TRIAL_DAYS}-day trial</span> only begins once your phone number is live.
              </p>
              <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-left text-sm space-y-2 max-w-md mx-auto">
                <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="font-semibold">{p?.name} · £{p?.price}/mo</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Business</span><span className="font-semibold">{cfg.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-semibold">{phone.option === "existing" ? (phone.business_number || "Existing number") : "OperaMind number"}</span></div>
              </div>

              {billing?.card_verified ? (
                <>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5" data-testid="card-verified-badge">
                    <ShieldCheck className="h-4 w-4" /> Payment method verified {billing.card?.last4 ? `· ${billing.card.brand} ••••${billing.card.last4}` : ""}
                  </div>
                  <div>
                    <Button data-testid="create-receptionist-btn" onClick={createReceptionist} disabled={busy} className="mt-6 bg-blue-600 hover:bg-blue-700 gap-2 h-11 px-8 text-base">
                      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Rocket className="h-4 w-4" /> Create My AI Receptionist</>}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="mt-5 rounded-xl border-2 border-blue-200 bg-blue-50/50 p-5 max-w-md mx-auto text-left" data-testid="onboard-payment-gate">
                  <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-600" /><h3 className="font-semibold text-slate-900">Verify your payment method</h3></div>
                  <p className="text-sm text-slate-600 mt-2">
                    A valid card is required before you create and test your AI. We'll take a <span className="font-semibold">£1 verification charge</span> now.
                    Your <span className="font-semibold">{TRIAL_DAYS}-day free trial</span> starts only when your phone number goes live, and your
                    <span className="font-semibold"> {p?.name}</span> plan (£{p?.price}/mo) begins automatically after the trial unless you cancel.
                  </p>
                  <div className="mt-4">
                    {billingConfig ? (
                      <SquareCardForm config={billingConfig} onToken={verifyCardToken} busy={busy} />
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading secure card form…</div>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2 text-center">Secured by Square · Your card details never touch our servers.</p>
                </div>
              )}
            </div>
          )}

          {step < STEPS.length - 1 && (
            <div className="mt-7 flex items-center justify-between">
              <Button variant="ghost" disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)} className="gap-2 text-slate-500"><ArrowLeft className="h-4 w-4" /> Back</Button>
              <Button data-testid="onboard-next" onClick={next} disabled={busy} className="bg-blue-600 hover:bg-blue-700 gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
